import { gridIndex, type LngLatBounds, type ModelGrid } from './grid';

/**
 * The wind API serves a model's grid in square blocks, like Windy's data tiles but in the model's own grid
 * (no resampling): block (bx, by) holds points [bx·BLOCK, bx·BLOCK + BLOCK] of each axis, so neighbouring
 * blocks share their edge points and interpolation never needs a neighbour.
 *
 * Level 0 is the native grid; level L keeps every 2^L-th point (averaged over the points around it), so a
 * zoomed-out view loads a few small blocks rather than the whole grid. A level depends on the model only,
 * never on the map zoom: the same place always shows the same values.
 */
export const BLOCK = 64;
/** Coarsest level: 2^3 points a side averaged into one. */
export const MAX_LEVEL = 3;
/** Levels stop once the grid's longer side has at most this many points. */
const COARSEST_SIDE = 512;

/** Number of levels a grid has (level 0 up to the result − 1). */
export function levelCount(g: ModelGrid): number {
  let level = 0;
  while (level < MAX_LEVEL && Math.max(g.nx, g.ny) / 2 ** level > COARSEST_SIDE) {
    // Grids around the world must split into whole columns, or the seam would move.
    if (g.kind === 'latlon' && g.wraps && g.nx % 2 ** (level + 1) !== 0) break;
    level++;
  }
  return level + 1;
}

/** The grid at a level: point (i, j) is point (i·2^level, j·2^level) of the native grid. */
export function levelGrid(g: ModelGrid, level: number): ModelGrid {
  const f = 2 ** level;
  const ny = Math.floor((g.ny - 1) / f) + 1;
  if (g.kind === 'latlon') {
    const nx = g.wraps ? g.nx / f : Math.floor((g.nx - 1) / f) + 1;
    return { ...g, nx, ny, dLon: g.dLon * f, dLat: g.dLat * f };
  }
  return { ...g, nx: Math.floor((g.nx - 1) / f) + 1, ny, dx: g.dx * f, dy: g.dy * f };
}

const wraps = (g: ModelGrid) => g.kind === 'latlon' && g.wraps;

/** Blocks along each axis of a (level) grid. Around the world, the last block also holds the seam. */
export function blockCounts(g: ModelGrid): { nbx: number; nby: number } {
  return {
    nbx: Math.max(1, Math.ceil((wraps(g) ? g.nx : g.nx - 1) / BLOCK)),
    nby: Math.max(1, Math.ceil((g.ny - 1) / BLOCK)),
  };
}

export interface BlockRef {
  bx: number;
  by: number;
  /** Turns of the world east (+) or west (−) to place the block at, for grids around the world. */
  turn: number;
}

/** Blocks [kMin, kMax] holding points a..b (inclusive) of an axis with `count` blocks. */
function blockSpan(a: number, b: number, count: number): [number, number] {
  const kMin = Math.min(count - 1, Math.max(0, Math.floor(a / BLOCK)));
  const kMax = Math.min(count - 1, Math.max(kMin, Math.ceil(b / BLOCK) - 1));
  return [kMin, kMax];
}

/** Fractional grid indices `bounds` span. Projected grids curve its edges, so points along them are taken. */
function indexRange(g: ModelGrid, bounds: LngLatBounds) {
  let i0 = Infinity,
    i1 = -Infinity,
    j0 = Infinity,
    j1 = -Infinity;
  const steps = g.kind === 'latlon' ? 1 : 8;
  for (let k = 0; k <= steps; k++) {
    const lng = bounds.w + ((bounds.e - bounds.w) * k) / steps;
    const lat = bounds.s + ((bounds.n - bounds.s) * k) / steps;
    for (const [i, j] of [
      gridIndex(g, lng, bounds.s),
      gridIndex(g, lng, bounds.n),
      gridIndex(g, bounds.w, lat),
      gridIndex(g, bounds.e, lat),
    ]) {
      i0 = Math.min(i0, i);
      i1 = Math.max(i1, i);
      j0 = Math.min(j0, j);
      j1 = Math.max(j1, j);
    }
  }
  return { i0, i1, j0, j1 };
}

/** Whether `bounds` lie entirely inside a regional grid (always true around the world, within its rows). */
export function gridContains(g: ModelGrid, bounds: LngLatBounds): boolean {
  const { i0, i1, j0, j1 } = indexRange(g, bounds);
  const columns = wraps(g) || (i0 >= 0 && i1 <= g.nx - 1);
  return columns && j0 >= 0 && j1 <= g.ny - 1;
}

/**
 * The blocks of a (level) grid covering `bounds`, with one point to spare on every side so interpolation
 * reaches the edges. Empty when the bounds miss the grid.
 */
export function blocksCovering(g: ModelGrid, bounds: LngLatBounds): BlockRef[] {
  const { i0, i1, j0, j1 } = indexRange(g, bounds);
  const a = Math.floor(i0) - 1;
  const b = Math.ceil(i1) + 1;
  const ja = Math.max(0, Math.floor(j0) - 1);
  const jb = Math.min(g.ny - 1, Math.ceil(j1) + 1);
  if (ja > jb) return [];
  const { nbx, nby } = blockCounts(g);
  const [byMin, byMax] = blockSpan(ja, jb, nby);

  const columns: { bx: number; turn: number }[] = [];
  if (wraps(g)) {
    // Each turn of the world the bounds reach into, at most once around.
    const last = Math.min(b, a + g.nx);
    for (let turn = Math.floor(a / g.nx); turn * g.nx <= last; turn++) {
      const from = Math.max(a - turn * g.nx, 0);
      const to = Math.min(last - turn * g.nx, g.nx);
      // Point nx is the next turn's point 0.
      if (from >= g.nx) continue;
      const [kMin, kMax] = blockSpan(from, to, nbx);
      for (let bx = kMin; bx <= kMax; bx++) columns.push({ bx, turn });
    }
  } else {
    const from = Math.max(0, a);
    const to = Math.min(g.nx - 1, b);
    if (from > to) return [];
    const [kMin, kMax] = blockSpan(from, to, nbx);
    for (let bx = kMin; bx <= kMax; bx++) columns.push({ bx, turn: 0 });
  }

  const blocks: BlockRef[] = [];
  for (let by = byMin; by <= byMax; by++) for (const c of columns) blocks.push({ ...c, by });
  return blocks;
}
